/**
 * WebGLServiceRadiusCircle — deck.gl ScatterplotLayer renderer for the service radius circle.
 * Replaces google.maps.Circle so the radius ring renders on the same GPU canvas as the
 * service markers (WebGLServiceMarkerLayer), avoiding Google Maps pane z-index conflicts.
 *
 * Renders at zIndex 5 — above the map tiles but below the marker layer (zIndex 10).
 */
import PropTypes from 'prop-types'
import { ScatterplotLayer } from '@deck.gl/layers'
import { DeckOverlay } from 'components/common/map/webgl/DeckOverlay'
import { hexToRgb } from 'components/common/map/webgl/generators/colorUtils'

// Below service markers (zIndex 10) but above map tiles
const DECK_STYLE = { zIndex: 5 }

const WebGLServiceRadiusCircle = ({ center, radius, strokeColor, strokeOpacity = 1, strokeWeight = 3, fillColor, fillOpacity = 0.3 }) => {
  if (!center || !radius || radius <= 0) return null

  // deck.gl layers must be fresh instances on every render — a finalized layer
  // (internalState set after DeckOverlay cleanup) cannot be reused.
  const fillRgba = [...hexToRgb(fillColor), Math.round(fillOpacity * 255)]
  const strokeRgba = [...hexToRgb(strokeColor), Math.round(strokeOpacity * 255)]

  const layer = new ScatterplotLayer({
    id: 'cs-service-radius',
    data: [{ position: [center.lng, center.lat] }],
    getPosition: d => d.position,
    getRadius: radius,
    radiusUnits: 'meters',
    getFillColor: fillRgba,
    getLineColor: strokeRgba,
    lineWidthMinPixels: strokeWeight,
    stroked: true,
    filled: true,
    pickable: false,
  })

  return <DeckOverlay layers={[layer]} style={DECK_STYLE} />
}

WebGLServiceRadiusCircle.propTypes = {
  center: PropTypes.shape({ lat: PropTypes.number, lng: PropTypes.number }),
  radius: PropTypes.number,
  strokeColor: PropTypes.string,
  strokeOpacity: PropTypes.number,
  strokeWeight: PropTypes.number,
  fillColor: PropTypes.string,
  fillOpacity: PropTypes.number,
}

export default WebGLServiceRadiusCircle
